import React, { useMemo } from 'react';

import { useGetCard, useGetFaces } from '@vlabs/api/point-api/client';
import { PinFilledIcon } from '@vlabs/icons';
import { ImageSlider } from '@vlabs/shared/components/image-slider/ImageSlider';
import { BaseSample } from '@vlabs/shared/components/sample/BaseSample';
import { Control, Tooltip } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { CardLink } from './CardLink';
import st from './MatchedCardInfo.module.sass';

function MatchedCardInfo({
  cardId,
}) {
  const { t } = useTranslation();
  const getCard = useGetCard(cardId, {}, { query: { enabled: !!cardId } });
  const getCardFaces = useGetFaces({ card_ids: [cardId] }, { query: { enabled: !!cardId } });

  const cardData = getCard.data;

  const orderedCardFaces = useMemo(() => {
    const cardFaces = getCardFaces.data?.faces ?? [];
    const mainCardFace = cardFaces.find(({ face_id }) => face_id === cardData?.main_face_id);

    if (!mainCardFace) return cardFaces;

    return [
      mainCardFace,
      ...cardFaces.filter(({ face_id }) => face_id !== mainCardFace.face_id),
    ];
  }, [cardData?.main_face_id, getCardFaces.data?.faces]);

  return (
    <div className={st.Root}>
      <p className={st.Title}>{t('lunapoint:карточка персоны.label')}</p>

      {!cardData && (
        <p className={st.Empty}>
          {t('lunapoint:карточка персоны.empty')}
        </p>
      )}

      {!!cardData && (
        <div className={st.Content}>
          <ImageSlider images={orderedCardFaces}>
            {(index) => {
              const face = orderedCardFaces[index];
              const avatarUrl = face?.avatar;

              return (
                <React.Fragment>
                  <BaseSample
                    className={st.Sample}
                    data-testid="imageSlider.cardFaceSample"
                    emptyClassName={st.Sample_empty}
                    imageUrl={avatarUrl ? `/api/lp5${avatarUrl}` : undefined}
                    type="face"
                  />

                  {!!face?.is_card_face && (
                    <div className={st.MainCardFaceMarker}>
                      <PinFilledIcon />
                    </div>
                  )}
                </React.Fragment>
              );
            }}
          </ImageSlider>

          <div className={st.Data}>
            <div className={st.Header}>{t('lunapoint:карточка персоны.ID карточки')}</div>
            <div data-testid="infoTab.cardSection.externalId">
              <CardLink cardId={cardData.card_id ?? cardId} />
            </div>

            <div className={st.Header}>{t('lunapoint:картотека.fio')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.cardSection.fio"
            >
              {cardData.user_data?.fio ?? '-'}
            </div>

            <div className={st.Header}>{t('lunapoint:карточка персоны.files.title')}</div>
            <div
              className={st.Value}
              data-testid="infoTab.cardSection.files"
            >
              {!cardData.files?.length && <span>-</span>}
              {cardData.files?.map((file) => (
                <Tooltip
                  key={file.file_id}
                  tooltipContent={file.filename}
                  tooltipProps={{ place: 'bottom' }}
                >
                  <Control.Link
                    className={st.Link}
                    target="_blank"
                    to={file.file_link}
                  >
                    {file.filename ?? file.file_link}
                  </Control.Link>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { MatchedCardInfo };
